import { useCallback, useEffect } from 'react';
import { MediaManagerSDKFile } from '../../../../typings/media-manager-sdk';
import {
  deleteFailedNotification,
  deleteSuccessNotification,
} from '../../../components/notifications';
import {
  FedopsInteractions,
  fedopsLogger,
} from '../../../services/fedopsLogger';
import { sentryClient } from '../../../services/sentryClient';
import { getVideoSessionFromTags } from '../../getVideoSessionFromTags';
import { useTrackVideoDeleteFinish } from '../bi/useTrackVideoDeleteFinish';
import { useFilesAction, useGetFilesData } from '../useFiles';
import { FilesStateActionType } from '../useFiles/useFiles.types';
import { useNotificatorConsumer } from '../useNotificator';
import { useWixmpDeleteFile } from '../usePrivateWixmp/usePrivateWixmp';
import { IUseDeleteAction } from './actions.types';

export const useDeleteAction = ({ mediaToken }: IUseDeleteAction) => {
  const { notify } = useNotificatorConsumer();
  const { trackVideoDeleteFinish } = useTrackVideoDeleteFinish();
  const { activeFile } = useGetFilesData();
  const { onFileAction } = useFilesAction();

  const { isLoading, initRequest, wixmpResponse, error } = useWixmpDeleteFile({
    mediaToken,
  });

  const removeFile = useCallback(() => {
    if (!activeFile) {
      return;
    }

    onFileAction({
      type: FilesStateActionType.Delete,
      payload: { files: [activeFile] },
    });
  }, [onFileAction, activeFile]);

  const deleteFileAction = useCallback(
    (file: MediaManagerSDKFile) => {
      fedopsLogger.interactionStarted(FedopsInteractions.Delete);

      void initRequest(file.id).then(() => {
        fedopsLogger.interactionEnded(FedopsInteractions.Delete);
      });
    },
    [initRequest],
  );

  useEffect(() => {
    if (isLoading || !activeFile) {
      return;
    }

    if (error) {
      sentryClient.captureException(error);
      notify(
        deleteFailedNotification({
          onTryAgain: () => {
            deleteFileAction(activeFile);
          },
          fileId: activeFile.id,
        }),
      );

      return;
    }

    if (wixmpResponse) {
      notify(deleteSuccessNotification({ fileId: activeFile.id }));
      removeFile();

      try {
        trackVideoDeleteFinish({
          videoId: activeFile.id,
          sessionId: getVideoSessionFromTags(activeFile.tags),
        });
      } catch (error) {
        sentryClient.captureException(error);
      }
    }
  }, [
    isLoading,
    error,
    wixmpResponse,
    notify,
    trackVideoDeleteFinish,
    removeFile,
    activeFile,
    mediaToken,
  ]);

  return { deleteFileAction, isLoading };
};
